/**
 * Baseline Service
 * Handles API calls for baseline management (BASELINE_ENABLED structure)
 */

import { api } from "./apiService";
import { getStoredUser, getStoredClientId } from "../utils/tokenUtils";

export const baselineService = {
  /**
   * Create a new baseline
   */
  createBaseline: async (baselineData) => {
    const user = getStoredUser();
    const storedClientId = getStoredClientId();
    const payload = {
      ...baselineData,
      client_id: storedClientId || user?.client_id,
    };
    const response = await api.post("/baseline/create", payload);
    return response.data;
  },

  /**
   * Get all baselines for a project
   */
  getBaselines: async (project_id, client_id, filters = {}) => {
    const user = getStoredUser();
    const storedClientId = getStoredClientId();
    const payload = {
      project_id,
      client_id: client_id || storedClientId || user?.client_id,
      ...filters,
    };
    const response = await api.post("/baseline/list", payload);
    return response.data;
  },

  /**
   * Get baseline by ID
   */
  getBaselineById: async (baseline_id, project_id, client_id) => {
    const user = getStoredUser();
    const storedClientId = getStoredClientId();
    const payload = {
      baseline_id,
      project_id,
      client_id: client_id || storedClientId || user?.client_id,
    };
    const response = await api.post("/baseline/get", payload);
    return response.data;
  },

  /**
   * Update a baseline
   */
  updateBaseline: async (baseline_id, project_id, client_id, updates) => {
    const user = getStoredUser();
    const storedClientId = getStoredClientId();
    const payload = {
      baseline_id,
      project_id,
      client_id: client_id || storedClientId || user?.client_id,
      ...updates,
    };
    const response = await api.post("/baseline/update", payload);
    return response.data;
  },

  /**
   * Publish a baseline
   */
  publishBaseline: async (
    baseline_id,
    project_id,
    client_id,
    force = false,
  ) => {
    const user = getStoredUser();
    const storedClientId = getStoredClientId();
    const payload = {
      baseline_id,
      project_id,
      client_id: client_id || storedClientId || user?.client_id,
      force,
    };
    const response = await api.post("/baseline/publish", payload);
    return response.data;
  },

  /**
   * Delete a baseline
   */
  deleteBaseline: async (baseline_id, project_id, client_id) => {
    const user = getStoredUser();
    const storedClientId = getStoredClientId();
    const payload = {
      baseline_id,
      project_id,
      client_id: client_id || storedClientId || user?.client_id,
    };
    const response = await api.post("/baseline/delete", payload);
    return response.data;
  },

  /**
   * Get baseline diff
   */
  getBaselineDiff: async (baseline_id, project_id, client_id) => {
    const user = getStoredUser();
    const storedClientId = getStoredClientId();
    const payload = {
      baseline_id,
      project_id,
      client_id: client_id || storedClientId || user?.client_id,
    };
    const response = await api.post("/baseline/diff", payload);
    return response.data;
  },

  /**
   * Request approval for baseline publish
   */
  requestBaselineApproval: async (baseline_id, project_id, client_id) => {
    const user = getStoredUser();
    const storedClientId = getStoredClientId();
    const payload = {
      baseline_id,
      project_id,
      client_id: client_id || storedClientId || user?.client_id,
    };
    const response = await api.post("/baseline/request-approval", payload);
    return response.data;
  },

  /**
   * Approve baseline publish request
   */
  approveBaselinePublish: async (
    approval_id,
    project_id,
    client_id,
    comments = null,
  ) => {
    const user = getStoredUser();
    const storedClientId = getStoredClientId();
    const payload = {
      approval_id,
      project_id,
      client_id: client_id || storedClientId || user?.client_id,
      comments,
    };
    const response = await api.post("/baseline/approve", payload);
    return response.data;
  },

  /**
   * Reject baseline publish request
   */
  rejectBaselinePublish: async (
    approval_id,
    project_id,
    client_id,
    comments,
  ) => {
    const user = getStoredUser();
    const storedClientId = getStoredClientId();
    const payload = {
      approval_id,
      project_id,
      client_id: client_id || storedClientId || user?.client_id,
      comments,
    };
    const response = await api.post("/baseline/reject", payload);
    return response.data;
  },

  /**
   * Get baseline approval requests
   */
  getBaselineApprovals: async (
    project_id,
    client_id,
    baseline_id = null,
    status = null,
  ) => {
    const user = getStoredUser();
    const storedClientId = getStoredClientId();
    const payload = {
      project_id,
      client_id: client_id || storedClientId || user?.client_id,
      baseline_id,
      status,
    };
    const response = await api.post("/baseline/approvals", payload);
    return response.data;
  },
};
